import { useEffect, useRef, useState } from "react";

interface Pin {
  id: string;
  name: string;
  x: number;
  y: number;
  /** Portugal and Italy sit close together on this map at every size we
   * render it at, so Italy's label is offset to the side instead of
   * stacking directly above its dot like the rest. */
  labelSide?: "top" | "right";
}

// coordinates are in the source SVG's own viewBox space (see VIEWBOX below),
// derived from each country path's bounding-box center, with Scotland nudged
// to the northern part of the Great Britain landmass (no separate ISO shape).
const PINS: Pin[] = [
  { id: "br", name: "Brasil", x: 275, y: 569 },
  { id: "ar", name: "Argentina", x: 262, y: 642 },
  { id: "py", name: "Paraguai", x: 268, y: 595 },
  { id: "pt", name: "Portugal", x: 388, y: 429 },
  { id: "gb-sct", name: "Escócia", x: 397, y: 372 },
  { id: "it", name: "Itália", x: 435, y: 421, labelSide: "right" },
];

const HIGHLIGHT_IDS = ["br", "ar", "py", "pt", "it", "gb"];

const VIEWBOX = { x: 30.767, y: 241.591, w: 784.077, h: 458.627 };

export default function WorldMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch("/world-map.svg")
      .then((res) => res.text())
      .then((svgText) => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = svgText;

        const svg = containerRef.current.querySelector("svg");
        if (!svg) return;
        svg.removeAttribute("width");
        svg.removeAttribute("height");
        svg.setAttribute("class", "w-full h-full");
        svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

        svg.querySelectorAll("path").forEach((p) => {
          p.setAttribute("fill", "rgba(69,182,218,0.14)");
          p.setAttribute("stroke", "rgba(255,255,255,0.12)");
          p.setAttribute("stroke-width", "0.6");
        });

        HIGHLIGHT_IDS.forEach((id) => {
          const el = svg.querySelector(`#${id}`);
          if (!el) return;
          // the id can sit on the <path> itself (e.g. Brazil) or on a
          // wrapping <g> (e.g. Argentina/Portugal/Italy/UK) — in the
          // group case the inner path's own fill wins, so target it too.
          const targets = el.tagName.toLowerCase() === "path" ? [el] : el.querySelectorAll("path");
          targets.forEach((t) => {
            t.setAttribute("fill", "rgba(248,155,55,0.9)");
            t.setAttribute("stroke", "rgba(248,155,55,1)");
          });
        });

        setReady(true);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <div className="relative w-full aspect-[784/459]">
        <div ref={containerRef} className="absolute inset-0" aria-hidden="true" />

        {ready &&
          PINS.map((pin, i) => {
            const left = ((pin.x - VIEWBOX.x) / VIEWBOX.w) * 100;
            const top = ((pin.y - VIEWBOX.y) / VIEWBOX.h) * 100;
            const isSide = pin.labelSide === "right";
            return (
              <div
                key={pin.id}
                className={`absolute -translate-x-1/2 -translate-y-1/2 flex items-center animate-fade-in ${
                  isSide ? "flex-row" : "flex-col"
                }`}
                style={{ left: `${left}%`, top: `${top}%`, animationDelay: `${i * 80}ms` }}
              >
                {/* labels crowd/overlap at phone widths since several of these
                    countries sit close together — shown from `sm:` up only,
                    with a legend list standing in on small screens instead. */}
                <span
                  className={`hidden sm:block text-[10px] font-mono-tech uppercase tracking-wide text-white bg-bg/90 border border-white/10 px-2 py-0.5 rounded whitespace-nowrap ${
                    isSide ? "ml-1.5" : "mb-1.5 -translate-y-full"
                  }`}
                >
                  {pin.name}
                </span>
                <span className="relative flex h-3 w-3 flex-shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-orange opacity-75 animate-ping"></span>
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-orange border border-white shadow-[0_0_10px_2px_rgba(248,155,55,0.7)]"></span>
                </span>
              </div>
            );
          })}
      </div>

      <div className="sm:hidden flex flex-wrap justify-center gap-2 mt-6">
        {PINS.map((pin) => (
          <span
            key={pin.id}
            className="inline-flex items-center gap-1.5 text-[11px] font-mono-tech uppercase tracking-wide text-white/80 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-orange"></span>
            {pin.name}
          </span>
        ))}
      </div>
    </div>
  );
}
